import PropTypes from "prop-types";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Button, DropdownMenu, Link, SkeletonLoader, Table } from "@yoast/ui-library";
import { PlusSmallIcon } from "@heroicons/react/24/solid";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import ProvisionedDate from "./ProvisionedDate";
import ProvisionedSubscriptionStatusBadge from "./ProvisionedSubscriptionStatusBadge";
import { canSetSite, isActionable } from "../../functions/provisionedSubscription";
import { provisionedSubscriptionShape } from "./provisionedSubscriptionShape";
import * as styles from "./ProvisioningStyles.module.scss";

const messages = defineMessages( {
	headerNumber: {
		id: "provisioning.list.header.number",
		defaultMessage: "Number",
	},
	headerEmail: {
		id: "provisioning.list.header.email",
		defaultMessage: "Email",
	},
	headerProduct: {
		id: "provisioning.list.header.product",
		defaultMessage: "Product",
	},
	headerSite: {
		id: "provisioning.list.header.site",
		defaultMessage: "Site",
	},
	headerStatus: {
		id: "provisioning.list.header.status",
		defaultMessage: "Status",
	},
	headerStart: {
		id: "provisioning.list.header.start",
		defaultMessage: "Start",
	},
	headerEnd: {
		id: "provisioning.list.header.end",
		defaultMessage: "End",
	},
	headerActions: {
		id: "provisioning.list.header.actions",
		defaultMessage: "Actions",
		description: "Visually hidden header label for the actions column.",
	},
	actionsFor: {
		id: "provisioning.list.actionsFor",
		defaultMessage: "Actions for subscription {number}",
	},
	renew: {
		id: "provisioning.list.action.renew",
		defaultMessage: "Extend with one billing term",
	},
	setSite: {
		id: "provisioning.list.action.setSite",
		defaultMessage: "Connect site",
	},
	cancel: {
		id: "provisioning.list.action.cancel",
		defaultMessage: "Cancel subscription",
	},
	refund: {
		id: "provisioning.list.action.refund",
		defaultMessage: "Mark as refunded",
	},
	empty: {
		id: "provisioning.list.empty",
		defaultMessage: "You haven't provisioned any subscriptions yet.",
	},
	noResults: {
		id: "provisioning.list.noResults",
		defaultMessage: "No subscriptions match \"{query}\".",
	},
	loadError: {
		id: "provisioning.list.loadError",
		defaultMessage: "We could not load your subscriptions. Please refresh the page.",
	},
	createButton: {
		id: "provisioning.list.createButton",
		defaultMessage: "New subscription",
	},
} );

/**
 * A placeholder row shown while the list loads for the first time.
 *
 * @returns {JSX.Element} A skeleton table row.
 */
function SkeletonRow() {
	return (
		<Table.Row>
			<Table.Cell><SkeletonLoader>YS-000000</SkeletonLoader></Table.Cell>
			<Table.Cell><SkeletonLoader>customer@example.com</SkeletonLoader></Table.Cell>
			<Table.Cell><SkeletonLoader>Yoast SEO Premium</SkeletonLoader></Table.Cell>
			<Table.Cell><SkeletonLoader>https://example.com</SkeletonLoader></Table.Cell>
			<Table.Cell><SkeletonLoader>Active</SkeletonLoader></Table.Cell>
			<Table.Cell><SkeletonLoader>Jan 1, 1970</SkeletonLoader></Table.Cell>
			<Table.Cell><SkeletonLoader>Jan 1, 1970</SkeletonLoader></Table.Cell>
			<Table.Cell><SkeletonLoader>Actions</SkeletonLoader></Table.Cell>
		</Table.Row>
	);
}

/**
 * The provisioned subscriptions table, with its loading, empty, no-results and error states.
 *
 * @param {Object} props Component props.
 *
 * @returns {JSX.Element} The table or its replacement state.
 */
function ProvisionedSubscriptionsTable(
	{
		subscriptions,
		query = "",
		isLoading = false,
		isError = false,
		onCreateClick,
		onRenew,
		onSetSite,
		onCancel,
		onRefund,
		intl,
	},
) {
	if ( isError ) {
		return (
			<Alert variant="error" role="alert">
				<FormattedMessage { ...messages.loadError } />
			</Alert>
		);
	}

	if ( ! isLoading && subscriptions.length === 0 ) {
		if ( query ) {
			return (
				<p className={ styles.empty }>
					<FormattedMessage { ...messages.noResults } values={ { query } } />
				</p>
			);
		}
		return (
			<div className={ styles.section }>
				<p className={ styles.empty }>
					<FormattedMessage { ...messages.empty } />
				</p>
				<Button variant="primary" onClick={ onCreateClick }>
					<PlusSmallIcon className={ styles.actionIcon } />
					<FormattedMessage { ...messages.createButton } />
				</Button>
			</div>
		);
	}

	return (
		<div className={ styles.tableContainer }>
			<Table>
				<Table.Head className={ styles.tableHead }>
					<Table.Row>
						<Table.Header><FormattedMessage { ...messages.headerNumber } /></Table.Header>
						<Table.Header><FormattedMessage { ...messages.headerEmail } /></Table.Header>
						<Table.Header><FormattedMessage { ...messages.headerProduct } /></Table.Header>
						<Table.Header><FormattedMessage { ...messages.headerSite } /></Table.Header>
						<Table.Header><FormattedMessage { ...messages.headerStatus } /></Table.Header>
						<Table.Header><FormattedMessage { ...messages.headerStart } /></Table.Header>
						<Table.Header><FormattedMessage { ...messages.headerEnd } /></Table.Header>
						<Table.Header>
							<span className="yst-sr-only">
								<FormattedMessage { ...messages.headerActions } />
							</span>
						</Table.Header>
					</Table.Row>
				</Table.Head>
				<Table.Body className={ styles.tableBody }>
					{ isLoading
						? <SkeletonRow />
						: subscriptions.map( subscription => (
							<Table.Row key={ subscription.id }>
								<Table.Cell data-label={ messages.headerNumber.defaultMessage }>
									<Link as={ RouterLink } to={ `/provisioning/subscriptions/${ subscription.id }` }>
										{ subscription.subscriptionNumber || subscription.id }
									</Link>
								</Table.Cell>
								<Table.Cell data-label={ messages.headerEmail.defaultMessage }>
									{ subscription.customerEmail || "—" }
								</Table.Cell>
								<Table.Cell data-label={ messages.headerProduct.defaultMessage }>
									{ ( subscription.product && subscription.product.name ) || "—" }
								</Table.Cell>
								<Table.Cell data-label={ messages.headerSite.defaultMessage }>
									{ subscription.siteUrl || "—" }
								</Table.Cell>
								<Table.Cell data-label={ messages.headerStatus.defaultMessage }>
									<ProvisionedSubscriptionStatusBadge status={ subscription.status } />
								</Table.Cell>
								<Table.Cell data-label={ messages.headerStart.defaultMessage }>
									<ProvisionedDate value={ subscription.startDate } />
								</Table.Cell>
								<Table.Cell data-label={ messages.headerEnd.defaultMessage }>
									<ProvisionedDate value={ subscription.endDate } />
								</Table.Cell>
								<Table.Cell className={ styles.actionsCell }>
									{ isActionable( subscription ) && <DropdownMenu>
										<DropdownMenu.IconTrigger
											screenReaderTriggerLabel={ intl.formatMessage(
												messages.actionsFor,
												{ number: subscription.subscriptionNumber || subscription.id },
											) }
										/>
										<DropdownMenu.List className={ styles.dropdownList }>
											<DropdownMenu.ButtonItem onClick={ () => onRenew( subscription ) }>
												<FormattedMessage { ...messages.renew } />
											</DropdownMenu.ButtonItem>
											{ canSetSite( subscription ) && (
												<DropdownMenu.ButtonItem onClick={ () => onSetSite( subscription ) }>
													<FormattedMessage { ...messages.setSite } />
												</DropdownMenu.ButtonItem>
											) }
											<DropdownMenu.ButtonItem
												className={ styles.dangerItem }
												onClick={ () => onCancel( subscription ) }
											>
												<FormattedMessage { ...messages.cancel } />
											</DropdownMenu.ButtonItem>
											<DropdownMenu.ButtonItem
												className={ styles.dangerItem }
												onClick={ () => onRefund( subscription ) }
											>
												<FormattedMessage { ...messages.refund } />
											</DropdownMenu.ButtonItem>
										</DropdownMenu.List>
									</DropdownMenu> }
								</Table.Cell>
							</Table.Row>
						) ) }
				</Table.Body>
			</Table>
		</div>
	);
}

ProvisionedSubscriptionsTable.propTypes = {
	subscriptions: PropTypes.arrayOf( provisionedSubscriptionShape ).isRequired,
	query: PropTypes.string,
	isLoading: PropTypes.bool,
	isError: PropTypes.bool,
	onCreateClick: PropTypes.func.isRequired,
	onRenew: PropTypes.func.isRequired,
	onSetSite: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
	onRefund: PropTypes.func.isRequired,
	intl: intlShape.isRequired,
};

export default injectIntl( ProvisionedSubscriptionsTable );
